import React, { useEffect, useMemo } from "react";
import { useFormContext, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AhuType } from "../../../models/unitType";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Checkbox from "@mui/material/Checkbox";
import Collapse from "@mui/material/Collapse";

function Fan() {
  const { control, watch, setValue, clearErrors } = useFormContext();
  const { t } = useTranslation();

  const Type = watch("Type");
  const coils = watch("coils");
  const EnergyVAV = watch("EnergyVAV");
  const Periphery = watch("Periphery");
  const AirflowLeading = watch("AirflowLeading");
  const PressureLeading = watch("PressureLeading");

  const hasHeater =
    (coils === "heater" || coils === "both" || coils === "heaterDefrost") &&
    Periphery > 0;
  const hasCooler = (coils === "cooler" || coils === "both") && Periphery > 0;
  const isCompactPrime = Type === AhuType.COMPACT || Type === AhuType.COMPACT_VERTICAL;

  const fanModeConfig = useMemo(() => {
    switch (Type) {
      case AhuType.COMPACT:
      case AhuType.COMPACT_VERTICAL:
        return {
          defaultValue: "Premium",
          disabled: false,
          options: ["Premium", "Standard"],
        };
      case AhuType.ENERGY_PRIME_STANDARD:
        return {
          defaultValue: "Premium",
          disabled: true,
          options: ["Premium"],
        };
      case AhuType.ENERGY_PRIME_GENIUS:
        return {
          defaultValue: "Premium",
          disabled: true,
          options: ["Premium"],
        };
      default:
        return {
          defaultValue: "Premium",
          disabled: true,
          options: ["Premium"],
        };
    }
  }, [Type]);

  useEffect(() => {
    setValue("fanMode", fanModeConfig.defaultValue, {
      shouldValidate: false,
    });
    setValue("fanAdvancedSettings", "On", { shouldValidate: false });
  }, [Type, setValue, fanModeConfig.defaultValue]);

  useEffect(() => {
    if (!EnergyVAV) {
      setValue("EnergyVAVCount", 0, { shouldValidate: false });
      clearErrors("EnergyVAVCount");
    }
  }, [EnergyVAV, setValue, clearErrors]);

  useEffect(() => {
    if (EnergyVAV) {
      setValue("AirflowLeading", false, { shouldValidate: false });
      setValue("PressureLeading", true, { shouldValidate: false });
    }
  }, [EnergyVAV, setValue]);

  useEffect(() => {
    if (!hasHeater) {
      setValue("HeaterMixingValve", false, { shouldValidate: false });
    }
    if (!hasCooler) {
      setValue("CoolerMixingValve", false, { shouldValidate: false });
    }
  }, [hasHeater, hasCooler, setValue]);

  const fanModeOptions = [
    { value: "Standard", label: t("fanType.Standard") },
    { value: "Premium", label: t("fanType.Premium") },
  ].filter((option) => fanModeConfig.options.includes(option.value));

  const handleAirflowLeadingChange = (checked) => {
    setValue("AirflowLeading", checked, { shouldValidate: false });
    if (checked) {
      setValue("PressureLeading", false, { shouldValidate: false });
    }
  };

  const handlePressureLeadingChange = (checked) => {
    setValue("PressureLeading", checked, { shouldValidate: false });
    if (checked) {
      setValue("AirflowLeading", false, { shouldValidate: false });
    }
  };

  const leftColumnOptions = [
    { name: "Automatics", label: t("options.automatics") },
    { name: "Energetics", label: t("options.energetics") },
    { name: "AirflowLeading", label: t("options.airflowLeading") },
    { name: "EnergyVAV", label: t("options.energyVav") },
    { name: "Periphery", label: t("options.periphery"), isSpecial: true },
  ];

  const rightColumnOptions = [
    { name: "ServiceOpening", label: t("options.serviceOpening") },
    { name: "Lights", label: t("options.lights") },
    { name: "Roof", label: t("options.roof") },
    { name: "SpareFilters", label: t("options.spareFilters") },
    { name: "PressureLeading", label: t("options.pressureLeading") },
  ];

  const renderCheckbox = (option) => {
    if (option.isSpecial) {
      return (
        <Box key={option.name}>
          <Controller
            name="Periphery"
            control={control}
            render={({ field: { value, onChange } }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={value > 0}
                    onChange={(e) => {
                      onChange(e.target.checked ? 2 : 0);
                    }}
                  />
                }
                label={option.label}
              />
            )}
          />
          <Collapse in={Periphery > 0}>
            <Box sx={{ ml: 4, mt: 1, mb: 1 }}>
              <Controller
                name="Periphery"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth size="small">
                    <InputLabel id="periphery-tier-label">
                      {t("options.peripheryTier.label")}
                    </InputLabel>
                    <Select
                      {...field}
                      value={field.value || ""}
                      labelId="periphery-tier-label"
                      label={t("options.peripheryTier.label")}
                      MenuProps={{ disableScrollLock: true }}
                    >
                      <MenuItem value={1}>
                        {t("options.peripheryTier.standard")}
                      </MenuItem>
                      <MenuItem value={2}>
                        {t("options.peripheryTier.premium")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
          </Collapse>
        </Box>
      );
    }

    if (option.name === "AirflowLeading") {
      return (
        <Box key={option.name}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!AirflowLeading}
                disabled={!!EnergyVAV}
                onChange={(e) => handleAirflowLeadingChange(e.target.checked)}
              />
            }
            label={option.label}
          />
        </Box>
      );
    }

    if (option.name === "PressureLeading") {
      return (
        <Box key={option.name}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!!PressureLeading}
                onChange={(e) => handlePressureLeadingChange(e.target.checked)}
              />
            }
            label={option.label}
          />
        </Box>
      );
    }

    return (
      <Box key={option.name}>
        <Controller
          name={option.name}
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!value}
                  onChange={(e) => onChange(e.target.checked)}
                />
              }
              label={option.label}
            />
          )}
        />
        {option.name === "EnergyVAV" && (
          <Collapse in={!!EnergyVAV}>
            <Box sx={{ ml: 4, mt: 1, mb: 1 }}>
              <Controller
                name="EnergyVAVCount"
                control={control}
                rules={
                  EnergyVAV
                    ? {
                        required:
                          t("validation.required") || "Required",
                        min: {
                          value: 1,
                          message:
                            t("validation.min", { min: 1 }) ||
                            "Must be at least 1",
                        },
                      }
                    : undefined
                }
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.onChange(
                        val === "" ? "" : parseInt(val, 10) || 0,
                      );
                    }}
                    fullWidth
                    size="small"
                    id="EnergyVAVCount"
                    label={t("options.energyVavCount")}
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    error={!!error}
                    helperText={error?.message}
                    inputProps={{ min: 1, step: 1 }}
                    InputProps={{
                      onWheel: (e) => e.target.blur(),
                    }}
                  />
                )}
              />
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  return (
    <Container component="main" maxWidth="xl" sx={{ mb: 4 }}>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, md: "15px 24px 24px 24px" } }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
          <Typography sx={{ textAlign: "center", fontSize: "38px", mb: "0px" }}>
            {t("mainform.fan")}
          </Typography>
        </Box>
        <Box sx={{ mt: 1 }}>
          <Divider sx={{ mt: 3, mb: 4 }} />
          {/* Fan Mode */}
          <Controller
            name="fanMode"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth>
                <InputLabel
                  id="select-label-fan-mode"
                  sx={{ mt: 1, color: "#373437" }}
                >
                  {t("fanType.label")}
                </InputLabel>
                <Select
                  {...field}
                  disabled={fanModeConfig.disabled}
                  disablePortal
                  MenuProps={{ disableScrollLock: true }}
                  fullWidth
                  sx={{ mt: 1, mb: 1 }}
                  labelId="select-label-fan-mode"
                  label={t("fanType.label")}
                  error={!!error}
                >
                  {fanModeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          {/* Supply pressure drop */}
          <Controller
            name="dropSupply"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                fullWidth
                id="dropSupply"
                label="Supply pressure drop"
                type="number"
                InputLabelProps={{ shrink: true }}
                helperText={error?.message}
                error={!!error}
                sx={{ mt: 2, mb: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">Pa</InputAdornment>
                  ),
                  onWheel: (e) => e.target.blur(),
                }}
              />
            )}
          />

          {/* Exhaust pressure drop */}
          <Controller
            name="dropReturn"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                value={field.value ?? ""}
                fullWidth
                id="dropReturn"
                label="Exhaust pressure drop"
                type="number"
                InputLabelProps={{ shrink: true }}
                helperText={error?.message}
                error={!!error}
                sx={{ mt: 2, mb: 1 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">Pa</InputAdornment>
                  ),
                  onWheel: (e) => e.target.blur(),
                }}
              />
            )}
          />
        </Box>
      </Paper>

      {/* Additional Equipment – Compact PRIME only */}
      {isCompactPrime && (
        <Paper
          variant="outlined"
          sx={{ p: { xs: 2, md: "15px 24px 24px 24px" }, mt: 3 }}
        >
          <Typography
            component="h2"
            variant="h5"
            sx={{ textAlign: "center", mb: 2 }}
          >
            {t("options.sectionTitle.additionalEquipment")}
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1,
            }}
          >
            {leftColumnOptions.map((option, index) => (
              <React.Fragment key={option.name}>
                {renderCheckbox(option)}
                {renderCheckbox(rightColumnOptions[index])}
              </React.Fragment>
            ))}
          </Box>

          {/* Mixing Valve section */}
          {(hasHeater || hasCooler) && (
            <>
              <Divider sx={{ mt: 3, mb: 2 }} />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 1,
                }}
              >
                {hasHeater && (
                  <Controller
                    name="HeaterMixingValve"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!value}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                        }
                        label={t("options.mixingValve.heater")}
                      />
                    )}
                  />
                )}
                {hasCooler && (
                  <Controller
                    name="CoolerMixingValve"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={!!value}
                            onChange={(e) => onChange(e.target.checked)}
                          />
                        }
                        label={t("options.mixingValve.cooler")}
                      />
                    )}
                  />
                )}
              </Box>
            </>
          )}
        </Paper>
      )}
    </Container>
  );
}

export default Fan;
